import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, XCircle, Clock, UserPlus, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserProfilePopover } from '@/components/ui/UserProfilePopover';
import { GroupBadge } from '@/components/guests/GroupBadge';
import { GuestSearchBar } from '@/components/guests/GuestSearchBar';
import { PersonalInvitationLink } from '@/components/guests/PersonalInvitationLink';
import type { Event } from '@/types/event.types';
import type { GuestGroup, GroupMembership } from '@/types/group.types';
import { guestRsvpLabel, isInvitationRecipientEligible, normalizeGuestRsvp, summarizeGuestRsvp, type GuestRow } from './guestRows';
export type { GuestRow } from './guestRows';

interface HostFilterOption {
  userId: number;
  label: string;
}

interface GuestListTabProps {
  events: Event[];
  guestRows: GuestRow[];
  groups: GuestGroup[];
  memberships: GroupMembership[];
  loading: boolean;
  showHostFilter?: boolean;
  hostFilterOptions?: HostFilterOption[];
  selectedHostUserId?: number | null;
  onHostFilterChange?: (hostUserId: number | null) => void;
  initialGroupFilter?: number | null;
  onGroupFilterConsumed?: () => void;
  canSendInvitations?: boolean;
  invitationReadinessMessage?: string;
  celebrationSlug?: string | null;
  onAddGuests?: () => void;
  selection?: {
    selectedGuestIds: number[];
    onSelectionChange: (ids: number[]) => void;
  };
}

export const GuestListTab: React.FC<GuestListTabProps> = ({
  events,
  guestRows,
  groups,
  memberships,
  loading,
  showHostFilter = false,
  hostFilterOptions = [],
  selectedHostUserId = null,
  onHostFilterChange,
  initialGroupFilter,
  onGroupFilterConsumed,
  onAddGuests,
  celebrationSlug,
  selection,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  const [selectedEventIds, setSelectedEventIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [defaultGroupApplied, setDefaultGroupApplied] = useState(false);
  const pageSize = 20;

  // By default, select the "all" system group so every guest is shown selected
  useEffect(() => {
    if (defaultGroupApplied || initialGroupFilter != null) return;
    const allGroup = groups.find(g => g.is_system_group && g.name === 'all');
    if (allGroup) {
      setSelectedGroupIds(new Set([allGroup.id]));
      setDefaultGroupApplied(true);
    }
  }, [groups, defaultGroupApplied, initialGroupFilter]);

  // Apply external group filter from Groups tab
  useEffect(() => {
    if (initialGroupFilter != null) {
      setSelectedGroupIds(new Set([initialGroupFilter]));
      setShowFilters(true);
      onGroupFilterConsumed?.();
    }
  }, [initialGroupFilter, onGroupFilterConsumed]);

  // Build guest -> groups mapping from memberships
  const guestGroupsMap = useMemo(() => {
    const map = new Map<number, Array<{ group_id: number; group_name: string; group_color: string | null }>>();
    for (const m of memberships) {
      if (!map.has(m.guest_id)) {
        map.set(m.guest_id, []);
      }
      map.get(m.guest_id)!.push({
        group_id: m.group_id,
        group_name: m.group_name,
        group_color: m.group_color,
      });
    }
    return map;
  }, [memberships]);

  // Include celebration roots and event records, including contact-only guests.
  const userIdToGuestIds = useMemo(() => {
    const map = new Map<number, Set<number>>();
    for (const row of guestRows) {
      const guestIds = new Set<number>([row.contactId]);
      for (const guest of row.eventGuests.values()) {
        if (guest?.id) guestIds.add(guest.id);
      }
      map.set(row.userId || -row.contactId, guestIds);
    }
    return map;
  }, [guestRows]);

  // Build userId -> groups (mapping through guest_ids)
  const userGroupsMap = useMemo(() => {
    const map = new Map<number, Array<{ group_id: number; group_name: string; group_color: string | null }>>();
    for (const [userId, guestIds] of userIdToGuestIds) {
      const userGroups: Array<{ group_id: number; group_name: string; group_color: string | null }> = [];
      const seenGroupIds = new Set<number>();
      for (const guestId of guestIds) {
        const groups = guestGroupsMap.get(guestId) || [];
        for (const g of groups) {
          if (!seenGroupIds.has(g.group_id)) {
            seenGroupIds.add(g.group_id);
            userGroups.push(g);
          }
        }
      }
      if (userGroups.length > 0) {
        map.set(userId, userGroups);
      }
    }
    return map;
  }, [userIdToGuestIds, guestGroupsMap]);

  // Build set of contactIds that match selected groups (OR logic)
  // Handles both system groups (rsvp-based) and custom groups (membership-based)
  const groupFilterContactIds = useMemo(() => {
    if (selectedGroupIds.size === 0) return null;

    // Separate selected groups into system and custom
    const selectedSystemGroups = groups.filter(g => g.is_system_group && selectedGroupIds.has(g.id));
    const selectedCustomGroupIds = new Set(
      groups.filter(g => !g.is_system_group && selectedGroupIds.has(g.id)).map(g => g.id)
    );

    const matchedContactIds = new Set<number>();

    // System group matching: filter by RSVP status
    for (const sysGroup of selectedSystemGroups) {
      if (sysGroup.name === 'all') {
        // "all" matches every guest
        for (const row of guestRows) matchedContactIds.add(row.contactId);
      } else if (sysGroup.name.startsWith('rsvp-')) {
        const status = sysGroup.name.replace('rsvp-', '');
        for (const row of guestRows) {
          if ((row.rsvpStatus ?? summarizeGuestRsvp(row.eventGuests.values())) === (status === 'mixed' ? 'mixed' : normalizeGuestRsvp(status))) matchedContactIds.add(row.contactId);
        }
      }
    }

    // Custom group matching: use memberships
    if (selectedCustomGroupIds.size > 0) {
      for (const [userId, userGroups] of userGroupsMap) {
        for (const g of userGroups) {
          if (selectedCustomGroupIds.has(g.group_id)) {
            // Find the GuestRow with this userId and add its contactId
            const row = guestRows.find(r => (r.userId || -r.contactId) === userId);
            if (row) matchedContactIds.add(row.contactId);
            break;
          }
        }
      }
    }

    return matchedContactIds;
  }, [selectedGroupIds, groups, guestRows, userGroupsMap]);

  // Filter guest rows
  const filteredRows = useMemo(() => {
    let filtered = guestRows;

    if (showHostFilter && selectedHostUserId) {
      filtered = filtered.filter((row) => row.hostUserId === selectedHostUserId);
    }

    const hasGroupFilter = selectedGroupIds.size > 0;
    const hasEventFilter = selectedEventIds.size > 0;

    if (hasGroupFilter || hasEventFilter) {
      filtered = filtered.filter((row) => {
        // OR logic: match if in ANY selected group OR invited to ANY selected event
        let matchesGroup = false;
        let matchesEvent = false;

        if (hasGroupFilter && groupFilterContactIds) {
          matchesGroup = groupFilterContactIds.has(row.contactId);
        }

        if (hasEventFilter) {
          for (const eventId of selectedEventIds) {
            if (row.eventGuests.has(eventId)) {
              matchesEvent = true;
              break;
            }
          }
        }

        // If both filters active, OR them. If only one, use that one.
        if (hasGroupFilter && hasEventFilter) return matchesGroup || matchesEvent;
        if (hasGroupFilter) return matchesGroup;
        return matchesEvent;
      });
    }

    // Text search (supports #groupname with logical operators)
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();

      if (query.includes('#')) {
        // Parse group expression with operators: or, and, ",", "|"
        // Tokenize: split by operators, keeping track of operator type
        const getContactIdsForGroup = (groupName: string): Set<number> => {
          const ids = new Set<number>();
          const matchingGroup = groups.find(g => g.name.toLowerCase() === groupName);
          if (!matchingGroup) return ids;

          if (matchingGroup.is_system_group) {
            if (matchingGroup.name === 'all') {
              for (const row of guestRows) ids.add(row.contactId);
            } else if (matchingGroup.name.startsWith('rsvp-')) {
              const status = matchingGroup.name.replace('rsvp-', '');
              for (const row of guestRows) {
                if ((row.rsvpStatus ?? summarizeGuestRsvp(row.eventGuests.values())) === (status === 'mixed' ? 'mixed' : normalizeGuestRsvp(status))) ids.add(row.contactId);
              }
            }
          } else {
            for (const [userId, userGroups] of userGroupsMap) {
              for (const g of userGroups) {
                if (g.group_id === matchingGroup.id) {
                  const row = guestRows.find(r => (r.userId || -r.contactId) === userId);
                  if (row) ids.add(row.contactId);
                  break;
                }
              }
            }
          }
          return ids;
        };

        // Split expression by operators: "or", "|", "," are OR; "and", "&" are AND
        // Tokenize into segments with their joining operator
        const segments: Array<{ groupName: string; operator: 'or' | 'and' }> = [];
        // Normalize separators: replace "|" and "," with " or ", "&" with " and "
        let normalized = query
          .replace(/\|/g, ' or ')
          .replace(/,/g, ' or ')
          .replace(/&/g, ' and ');
        // Split by " or " and " and ", keeping the delimiter
        const parts = normalized.split(/(\s+or\s+|\s+and\s+)/);

        let currentOperator: 'or' | 'and' = 'or';
        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed) continue;
          if (/^or$/i.test(trimmed)) { currentOperator = 'or'; continue; }
          if (/^and$/i.test(trimmed)) { currentOperator = 'and'; continue; }
          // Extract group name from #groupname
          const hashMatch = trimmed.match(/#([\w-]+)/);
          if (hashMatch) {
            segments.push({ groupName: hashMatch[1], operator: currentOperator });
          }
        }

        if (segments.length > 0) {
          // Evaluate: start with first group's contacts, then apply operators
          let resultIds = getContactIdsForGroup(segments[0].groupName);

          for (let i = 1; i < segments.length; i++) {
            const seg = segments[i];
            const segIds = getContactIdsForGroup(seg.groupName);
            if (seg.operator === 'or') {
              // Union
              for (const id of segIds) resultIds.add(id);
            } else {
              // Intersection
              const intersection = new Set<number>();
              for (const id of resultIds) {
                if (segIds.has(id)) intersection.add(id);
              }
              resultIds = intersection;
            }
          }

          filtered = filtered.filter(row => resultIds.has(row.contactId));
        } else {
          filtered = [];
        }
      } else {
        // Regular text search by name or email
        filtered = filtered.filter((row) =>
          row.name.toLowerCase().includes(query) ||
          row.email?.toLowerCase().includes(query)
        );
      }
    }

    return filtered;
  }, [guestRows, searchQuery, selectedGroupIds, selectedEventIds, groupFilterContactIds, showHostFilter, selectedHostUserId, groups, userGroupsMap]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedGroupIds, selectedEventIds, selectedHostUserId]);

  // Pagination
  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  const selectedIds = new Set(selection?.selectedGuestIds ?? []);
  const eligiblePageRows = paginatedRows.filter(isInvitationRecipientEligible);
  const eligibleMatchingRows = filteredRows.filter(isInvitationRecipientEligible);
  const allPageSelected = eligiblePageRows.length > 0 && eligiblePageRows.every(row => selectedIds.has(row.contactId));
  const somePageSelected = eligiblePageRows.some(row => selectedIds.has(row.contactId));
  const updateSelection = (rows: GuestRow[], checked: boolean) => {
    const next = new Set(selection?.selectedGuestIds ?? []);
    for (const row of rows) {
      if (!isInvitationRecipientEligible(row)) continue;
      if (checked) next.add(row.contactId);
      else next.delete(row.contactId);
    }
    selection?.onSelectionChange([...next]);
  };

  const toggleGroupFilter = (groupId: number) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleEventFilter = (eventId: number) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedGroupIds(new Set());
    setSelectedEventIds(new Set());
    onHostFilterChange?.(null);
  };

  const hasActiveFilters =
    selectedGroupIds.size > 0 ||
    selectedEventIds.size > 0 ||
    (showHostFilter && selectedHostUserId !== null);
  const showEmptyAddGuestsAction =
    Boolean(onAddGuests) &&
    guestRows.length === 0;

  const getInvitationIcon = (status: string) => {
    switch (normalizeGuestRsvp(status)) {
      case 'yes': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'no': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'maybe': return <Clock className="w-4 h-4 text-amber-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusLabel = guestRsvpLabel;

  const getEventStatusStyle = (status: string) => {
    switch (normalizeGuestRsvp(status)) {
      case 'yes': return 'bg-green-100 text-green-700 border-green-200';
      case 'no': return 'bg-red-100 text-red-700 border-red-200';
      case 'maybe': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const systemGroups = groups.filter(g => g.is_system_group);
  const customGroups = groups.filter(g => !g.is_system_group);

  if (loading) return null;

  return (
    <div>
      {/* Search + Event Checkboxes (shared component) */}
      <div className="flex items-center gap-2 mb-0">
        <div className="flex-1">
          <GuestSearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            events={events}
            selectedEventIds={selectedEventIds}
            onToggleEvent={toggleEventFilter}
            onClearEvents={() => setSelectedEventIds(new Set())}
            groups={groups}
          />
        </div>
        <div className="self-start pt-0">
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="relative"
          >
            <Filter className="h-3 w-3 mr-1" />
            Filters
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-blue-600 text-white text-[10px] rounded-full flex items-center justify-center">
                {selectedGroupIds.size + selectedEventIds.size + (showHostFilter && selectedHostUserId !== null ? 1 : 0)}
              </span>
            )}
          </Button>
        </div>
      </div>

        {/* Active Filter Summary */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mt-2 mb-4 flex-wrap">
            <span className="text-xs text-muted-foreground">Filtered by:</span>
            {Array.from(selectedGroupIds).map(gid => {
              const group = groups.find(g => g.id === gid);
              return group ? (
                <GroupBadge
                  key={`g-${gid}`}
                  name={group.name}
                  color={group.color}
                  active
                  size="sm"
                  onClick={() => toggleGroupFilter(gid)}
                  onRemove={() => toggleGroupFilter(gid)}
                />
              ) : null;
            })}
            {Array.from(selectedEventIds).map(eid => {
              const event = events.find(e => e.id === eid);
              return event ? (
                <span
                  key={`e-${eid}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 cursor-pointer hover:bg-amber-200"
                  onClick={() => toggleEventFilter(eid)}
                >
                  {event.title}
                  <X className="h-3 w-3" />
                </span>
              ) : null;
            })}
            {showHostFilter && selectedHostUserId !== null && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-200 cursor-pointer hover:bg-cyan-200"
                onClick={() => onHostFilterChange?.(null)}
              >
                Host: {hostFilterOptions.find((option) => option.userId === selectedHostUserId)?.label || 'Unknown'}
                <X className="h-3 w-3" />
              </span>
            )}
            <button onClick={clearFilters} className="text-xs text-blue-600 hover:underline ml-1">
              Clear all
            </button>
            <span className="text-xs text-muted-foreground">({filteredRows.length} results)</span>
          </div>
        )}

      {/* Collapsible Filter Panel */}
      {showFilters && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className={showHostFilter ? 'grid grid-cols-1 md:grid-cols-3 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
            {/* Groups Filter */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Groups</h4>
              {systemGroups.length > 0 && (
                <div className="mb-2">
                  <span className="text-[10px] text-gray-400 uppercase">System</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {systemGroups.map(group => (
                      <GroupBadge
                        key={group.id}
                        name={group.name}
                        color={group.color}
                        isSystem
                        count={group.member_count}
                        active={selectedGroupIds.has(group.id)}
                        onClick={() => toggleGroupFilter(group.id)}
                        size="sm"
                      />
                    ))}
                  </div>
                </div>
              )}
              {customGroups.length > 0 && (
                <div>
                  <span className="text-[10px] text-gray-400 uppercase">Custom</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {customGroups.map(group => (
                      <GroupBadge
                        key={group.id}
                        name={group.name}
                        color={group.color}
                        count={group.member_count}
                        active={selectedGroupIds.has(group.id)}
                        onClick={() => toggleGroupFilter(group.id)}
                        size="sm"
                      />
                    ))}
                  </div>
                </div>
              )}
              {groups.length === 0 && (
                <p className="text-xs text-gray-400 italic">No groups available</p>
              )}
            </div>

            {/* Events Filter */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Events</h4>
              <div className="space-y-1">
                {events.map(event => (
                  <label key={event.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded px-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedEventIds.has(event.id)}
                      onChange={() => toggleEventFilter(event.id)}
                      className="h-3.5 w-3.5 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">{event.title}</span>
                  </label>
                ))}
                {events.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No events available</p>
                )}
              </div>
            </div>

            {showHostFilter && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Host</h4>
                <select
                  title="Filter guests by host"
                  value={selectedHostUserId ?? ''}
                  onChange={(event) => onHostFilterChange?.(event.target.value ? Number(event.target.value) : null)}
                  className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">All Hosts</option>
                  {hostFilterOptions.map((option) => (
                    <option key={option.userId} value={option.userId}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results count */}
      {!hasActiveFilters && (
        <div className="mb-2 text-sm text-gray-600">
          Showing {filteredRows.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredRows.length)} of {filteredRows.length} guests
        </div>
      )}

      {selection && (
        <div className="my-3 flex flex-wrap items-center gap-3" aria-label="Recipient selection">
          <span role="status" className="text-sm text-gray-600">{selectedIds.size} selected</span>
          <Button type="button" variant="outline" size="sm" disabled={eligibleMatchingRows.length === 0} onClick={() => updateSelection(eligibleMatchingRows, true)}>
            Select all matching
          </Button>
          <Button type="button" variant="ghost" size="sm" disabled={selectedIds.size === 0} onClick={() => selection.onSelectionChange([])}>
            Clear selection
          </Button>
          <span className="text-xs text-muted-foreground">Only primary guests with an email address can be selected.</span>
        </div>
      )}

      {/* Guest Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {selection && (
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select current page"
                      checked={allPageSelected}
                      ref={input => { if (input) input.indeterminate = somePageSelected && !allPageSelected; }}
                      disabled={eligiblePageRows.length === 0}
                      onChange={event => updateSelection(eligiblePageRows, event.target.checked)}
                    />
                  </th>
                )}
                <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  Guest Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  Groups
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  Events
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  RSVP
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  Personal Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Host
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={selection ? 8 : 7} className="px-6 py-12 text-center text-gray-500">
                    {showEmptyAddGuestsAction ? (
                      <div className="flex flex-col items-center gap-3">
                        <span>No guests found. Add contacts to see them here.</span>
                        <Button size="sm" onClick={onAddGuests}>
                          <UserPlus className="h-4 w-4 mr-1" />
                          Add guests
                        </Button>
                      </div>
                    ) : searchQuery || hasActiveFilters ? (
                      'No guests match your filters.'
                    ) : (
                      'No guests found. Add contacts to see them here.'
                    )}
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => {
                  const rowGroups = userGroupsMap.get(row.userId || -row.contactId) || [];
                  const rowEvents = events.filter(e => row.eventGuests.has(e.id));
                  const rsvpSummary = row.rsvpStatus ?? summarizeGuestRsvp(row.eventGuests.values());

                  return (
                    <tr key={row.contactId} className="hover:bg-gray-50">
                      {selection && (
                        <td className="px-3 py-4">
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.name}`}
                            title={isInvitationRecipientEligible(row) ? `Select ${row.name}` : 'Requires a primary guest with an email address'}
                            checked={selectedIds.has(row.contactId)}
                            disabled={!isInvitationRecipientEligible(row)}
                            onChange={event => updateSelection([row], event.target.checked)}
                          />
                        </td>
                      )}
                      {/* Guest Name */}
                      <td className="sticky left-0 z-10 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        <UserProfilePopover
                          rsvpContext={rowEvents.map(event => ({ eventId: event.id, eventTitle: event.title, status: row.eventGuests.get(event.id)?.rsvp_status }))}
                          user={{
                            id: row.userId || 0,
                            full_name: row.name,
                            email: row.email || '',
                            phone: row.phone,
                          }}
                        >
                          <span className="hover:underline cursor-pointer">
                            {row.name}
                            {row.nickname && (
                              <span className="text-xs text-gray-500 ml-1">({row.nickname})</span>
                            )}
                          </span>
                        </UserProfilePopover>
                      </td>

                      {/* Lifecycle status */}
                      <td className="px-6 py-4 text-sm border-r border-gray-200">
                        <div className="flex flex-wrap gap-1">
                          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                            Added
                          </span>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                            row.assignedEventCount > 0
                              ? 'border-amber-200 bg-amber-50 text-amber-700'
                              : 'border-dashed border-gray-200 text-gray-400'
                          }`}>
                            Assigned
                          </span>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                            row.isInvited
                              ? 'border-blue-200 bg-blue-50 text-blue-700'
                              : 'border-dashed border-gray-200 text-gray-400'
                          }`}>
                            Invited
                          </span>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                            row.hasResponded
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-dashed border-gray-200 text-gray-400'
                          }`}>
                            RSVP
                          </span>
                        </div>
                      </td>

                      {/* Groups */}
                      <td className="px-6 py-4 text-sm border-r border-gray-200">
                        <div className="flex flex-wrap gap-1">
                          {rowGroups.length > 0 ? (
                            rowGroups.map((g) => (
                              <GroupBadge
                                key={g.group_id}
                                name={g.group_name}
                                color={g.group_color || undefined}
                                size="sm"
                                active={selectedGroupIds.has(g.group_id)}
                                onClick={() => toggleGroupFilter(g.group_id)}
                              />
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>

                      {/* Events */}
                      <td className="px-6 py-4 text-sm border-r border-gray-200">
                        <div className="flex flex-wrap gap-1">
                          {rowEvents.length > 0 ? (
                            rowEvents.map((e) => (
                              (() => {
                                const eventGuest = row.eventGuests.get(e.id);
                                const eventStatus = eventGuest?.rsvp_status || 'new';
                                return (
                              <span
                                key={e.id}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border cursor-pointer ${
                                  selectedEventIds.has(e.id)
                                    ? 'bg-amber-500 text-white border-amber-600'
                                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                                }`}
                                onClick={() => toggleEventFilter(e.id)}
                              >
                                <span className="mr-1">{e.title}</span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] border ${getEventStatusStyle(eventStatus)}`}>
                                  {getStatusLabel(eventStatus)}
                                </span>
                              </span>
                                );
                              })()
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>

                      {/* RSVP */}
                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-200">
                        <div className="flex items-center justify-center gap-1">
                          {getInvitationIcon(rsvpSummary)}
                          <span className="text-xs text-gray-600">{getStatusLabel(rsvpSummary)}</span>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-200">
                        <PersonalInvitationLink
                          slug={celebrationSlug}
                          accessToken={row.accessToken}
                          name={row.name}
                        />
                      </td>

                      {/* Host */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {row.hostName || row.creatorName ? (
                          <div className="space-y-1">
                            {row.hostName ? (
                              <div className="flex items-center gap-1">
                                <span>{row.hostName}</span>
                                {row.hostNickname ? (
                                  <span className="text-xs text-gray-500">({row.hostNickname})</span>
                                ) : null}
                              </div>
                            ) : null}
                            {row.creatorName && row.creatorName !== row.hostName ? (
                              <div className="text-xs text-gray-500">Added by {row.creatorName}</div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
